// ─────────────────────────────────────────────────────────────────────────────
// RF-130 — "Mi Wrapped" (Recap anual de reconocimiento), overlay del titular.
//
// QUÉ GOBIERNA ESTA PANTALLA. Los slides NO viven aquí: salen de la edición vigente del Recap
// (`GET v1/recap/current` → `RecapEditionDto.slides`), que es lo que el admin edita en
// Admin › Recap. De la configuración vienen el FONDO, el ICONO, el TÍTULO en el idioma de la sesión
// y las ETIQUETAS de los números; de la plataforma vienen los DATOS (`WrappedDto`, racha, feed,
// ganadores). El admin nunca captura cifras del colaborador y el cliente nunca inventa copy.
//
// Hasta RF-130 este overlay armaba 7-8 slides propios con un vocabulario que no existía en ningún
// contrato (`intro, totals, fund, people, collab, streak, generosity, final`) y con los gradientes
// literales del demo: la pantalla de administración del Recap no afectaba a NADA de lo que veía el
// colaborador. El catálogo real son 9 slides fijos y configurables (`RecapSlideCatalog.cs`):
// portada · numeros · define · mensaje · quienes · red · constancia · reto · ganador.
//
// REGLAS QUE NO SE PUEDEN RELAJAR:
//   · Los slides llegan YA filtrados por `activo` y ordenados por `indice`
//     (`GetCurrentRecapQueryHandler.cs:21-24`). Aquí NO se re-filtran ni se re-ordenan: duplicar esa
//     regla en dos sitios es garantizar que un día discrepen.
//   · Slide sin dato ⇒ SE OMITE (RF-127). Nunca un 0, nunca un "—" de relleno. Ése fue exactamente
//     el bug de la racha apagada: el Recap anunciaba "Tu racha más larga · 0 semanas", una cifra
//     falsa de una funcionalidad que el RF manda ocultar.
//   · `soloGenerico` (ingreso muy tardío) ⇒ UNA sola tarjeta con el mensaje genérico de la edición,
//     y cero slides de datos: unas semanas no dan para un resumen anual.
//   · Sin puntos, en ningún slide (regla de oro #1 del módulo).
//
// El popup de aviso `.wrp-*` SÍ tiene consumidor: es el teaser "tu Recap está listo" que pinta
// `PerfilView.tsx:749`. (Los comentarios anteriores afirmaban lo contrario, y también que "se
// mantienen los 5 slides" cuando eran 7-8; ambas cosas llevaban meses siendo falsas.)
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { nombreReparado, useReparadorDeNombres } from './nombresDeReconocimiento';
import { useTranslation } from 'react-i18next';
import { estiloDeFondo } from './recapFondo';
import {
  getFundamentalName,
  pickLocalized,
  useCurrentRecap,
  useMyWrapped,
  useRecognitions,
  useUserById,
  useWinners,
  useCampaignBadges,
  useWrappedAvailability,
} from '@/api/recognition';
import type {
  FundamentalDto,
  RecapGenericMessageDto,
  RecapSlideDto,
  RecognitionDto,
  StreakPeriodicity,
  WinnerDto,
  AdminBadgeDto,
  WrappedAvailabilityDto,
  WrappedDto,
} from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { weeksKeyOf } from '@/utils/weeksPlural';
import { streakUnitCopy } from '@/utils/streakUnit';

/** Clave del slide del catálogo cuyo icono NO es editable: usa la insignia real del ganador. */
const AUTO_ICON_KEY = 'ganador';

/**
 * Slides que NUNCA se omiten por falta de dato: su contenido base ES la configuración (título/icono
 * del admin), así que no hay "dato vacío" que los pueda dejar fuera. `portada` abre el Recap.
 *
 * Bug Sesión #80 — `reto` se queda en esta lista aunque desde este fix SÍ puede llevar una cifra
 * inyectada (`bottomFundamentalId`, ver `renderReto`): sigue sin tener un "hueco" que tape, porque su
 * llamado a la acción genérico se sostiene solo incluso sin Fundamental que concatenar (titular sin
 * envíos en el año). Por eso no cuenta como slide de datos para la regla "todo se omitió → tarjeta
 * genérica" (`dataSlides`, más abajo).
 */
const CONFIG_ONLY_KEYS = new Set(['portada', 'reto']);

/**
 * RF-130 corrección F1 (H-06) — `etiquetaGenerosidad` es una CLAVE ESTABLE que manda Analytics
 * (`GetRecognitionWrappedQueryHandler.cs`), no texto: `superGeneroso` / `muyGeneroso` / `generoso`.
 * Lista blanca explícita, a propósito: si se cambiara por `t(w.etiquetaGenerosidad)` con
 * `defaultValue` igual a la clave cruda, un backend por delante del cliente (una clave nueva que el
 * cliente aún no traduce) volvería a pintar la cadena en pantalla — exactamente el defecto que esto
 * corrige. Con la lista blanca, una clave desconocida simplemente no pinta nada (RF-127).
 */
const GENEROSITY_TAG_KEYS = new Set(['generoso', 'muyGeneroso', 'superGeneroso']);

interface Props {
  catalog: FundamentalDto[];
  lang: string;
  onClose: () => void;
  /**
   * Racha más larga tomada de StreakDto.record — el Wrapped no la trae.
   *
   * RF-043 — `undefined` significa "no hay racha que enseñar" (apagada en Gestión, o sin datos), y
   * entonces el slide `constancia` NO se pinta. No lo sustituyas por un `?? 0`.
   */
  streakRecord?: number;
  /**
   * RF-037 — Período en que está contado `streakRecord`. Sin él, el Wrapped rotulaba "N semanas"
   * sobre un récord que con periodicidad mensual son meses.
   */
  streakPeriodicidad?: StreakPeriodicity | null;
  /**
   * RF-130 — Disponibilidad ya resuelta por el llamador (el Perfil la necesita para decidir si
   * monta siquiera el botón). Pasarla evita volver a razonarla; sin ella el overlay la consulta por
   * su cuenta, contra la misma clave de caché.
   */
  availability?: WrappedAvailabilityDto | null;
}

/** Un slide listo para pintar: la configuración ya resuelta + el contenido con los datos dentro. */
interface RenderedSlide {
  key: string;
  /** Estilo de fondo ya resuelto: color, o imagen sobre el color (`estiloDeFondo`). */
  fondo: CSSProperties;
  content: ReactNode;
}

/** ¿El icono configurado es una imagen (URL) o un emoji? */
function isImageIcon(icono: string | null | undefined): boolean {
  if (!icono) return false;
  return icono.startsWith('http://') || icono.startsWith('https://') || icono.startsWith('/');
}

/**
 * Mejor mensaje recibido del año — GAP-007, resuelto EN EL CLIENTE sobre el feed que el perfil ya
 * trae (sin llamadas nuevas). Prioridad: más Fundamentales en un solo reconocimiento y, a igualdad,
 * el mensaje más largo. `MejorMensajeId` del contrato no se usa: Analytics no tiene ni el texto ni
 * las reacciones, y lo deja siempre en null.
 *
 * RF-130 M1 (H-12) — el año se lee en UTC (`getUTCFullYear`), NUNCA en el huso local del navegador:
 * `anio` es el año que ya cortó el backend sobre `RecognitionUserAggregate.YearKey`
 * (AnalyticsService), que agrupa por año calendario UTC. Comparar con `getFullYear()` (local)
 * desalinea el filtro del backend: un reconocimiento del 1-ene a las 02:00 UTC es del año N para el
 * servidor, pero `getFullYear()` en México (UTC-6) lo lee como 31-dic del año N-1 — el mismo dato
 * contaría en las cifras de un año y en este slide del otro.
 */
function pickBestMessage(items: readonly RecognitionDto[], anio: number): RecognitionDto | null {
  let best: RecognitionDto | null = null;
  for (const r of items) {
    if (!r.mensaje) continue;
    if (new Date(r.fecha).getUTCFullYear() !== anio) continue;
    if (best === null) { best = r; continue; }
    const funds = r.fundamentalIds.length;
    const bestFunds = best.fundamentalIds.length;
    if (funds > bestFunds || (funds === bestFunds && r.mensaje.length > best.mensaje.length)) best = r;
  }
  return best;
}

/**
 * Ganador anual del titular, si lo hay. "No saldrá insignia de ganador si no se cargaron ganadores":
 * sin registro para esta persona el slide se omite entero.
 *
 * 🔴 `anio` MANDA sobre `registradoAt` (bug Sesión #79): `registradoAt` es cuándo un administrador
 * dio de alta la fila, no el año del premio. Comparar contra esa fecha desubicaba al ganador de 2025
 * registrado en enero de 2026 (desaparecía de su propio Wrapped y salía en el del año siguiente).
 *
 * Sin `anio` (carga histórica, Fase 3/D-10, que nunca captura ese campo) el ganador se DESCARTA —
 * segunda vuelta de #79: el respaldo a `registradoAt` que existía antes colapsaba TODOS los
 * históricos en el año en que se importaron, que casi nunca es el año real del premio. Sin un año
 * confiable, la decisión de negocio es omitir el slide antes que mostrar un año inventado.
 *
 * 🔴 Bug #87 — un ganador `Pendiente` (recién importado por CSV, sin que un admin le haya dado clic a
 * "Confirmar") tampoco cuenta: "eres ganador" es un hecho publicable, y Pendiente significa que
 * todavía no lo es. `undefined`/`null` se trata como Confirmado (default del dominio para ganadores
 * anteriores a CPE-134, que nunca traen este campo) — solo se excluye el valor explícito
 * `'Pendiente'`.
 */
function findMyWinner(
  winners: readonly WinnerDto[],
  myId: string | undefined,
  anio: number,
  badges: readonly AdminBadgeDto[],
): WinnerDto | null {
  if (!myId) return null;
  // Bug #87 — el filtro de Pendiente va DENTRO del mismo `.find`, junto con `anio`: es la misma
  // condición de "¿esto es un hecho publicable?" que la fila 40 aplica más abajo con la insignia,
  // no un paso aparte.
  const mio = winners.find((w) => w.userId === myId && w.anio === anio && w.estado !== 'Pendiente');
  if (!mio) return null;

  // Fila 40 — segunda condición: la MEDALLA también tiene que ser del año.
  //
  // `w.anio` sale de la campaña, y la insignia se elige aparte: hasta que la fila 40 los confrontó en
  // el alta, se podía otorgar la medalla de 2024 dentro de una campaña de 2026 y quedaba un ganador
  // con `anio = 2026` luciendo una medalla de otro año. La validación de arriba impide crear nuevos,
  // pero los que YA están guardados en QA y producción siguen ahí, y ninguna corrección retroactiva
  // los alcanza: la comprobación aquí es lo que evita que el Wrapped de 2026 siga anunciando GANADOR
  // a quien no ganó ese año.
  //
  // Solo descarta cuando la insignia AFIRMA otro año. Sin insignia, insignia que no está en el
  // catálogo, o insignia sin año declarado (todas las anteriores a la fila 40), no hay contradicción
  // que detectar y el ganador se muestra — descartarlo sería esconder premios legítimos por un dato
  // que nunca se capturó, que es el mismo error que la segunda vuelta del #79 ya corrigió.
  const insignia = mio.badgeId ? badges.find((b) => b.id === mio.badgeId) : undefined;
  if (insignia?.anio != null && insignia.anio !== anio) return null;

  return mio;
}

/** ¿El mensaje genérico de la edición tiene algo que decir? Sembrado nace VACÍO (nada inventado). */
function hasGenericContent(msg: RecapGenericMessageDto | undefined, lang: string): boolean {
  if (!msg) return false;
  return Boolean(pickLocalized(msg.titulo, lang) || pickLocalized(msg.descripcion, lang));
}

export function WrappedModal({
  catalog,
  lang,
  onClose,
  streakRecord,
  streakPeriodicidad,
  availability: availabilityProp,
}: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const myId = demoUser.userId || undefined;
  const ouId = demoUser.ou || undefined;
  const [slide, setSlide] = useState(0);

  // El llamador (el Perfil) ya resolvió la disponibilidad para gatear el botón; si no la pasó, se
  // consulta aquí. Nunca se asume "disponible": ése era el gate constante `true` que el RF corrige.
  const availabilityQuery = useWrappedAvailability(availabilityProp == null);
  const availability = availabilityProp ?? availabilityQuery.data;
  const disponible = availability?.disponible === true;
  const anio = availability?.anio ?? 0;
  const soloGenerico = availability?.soloGenerico === true;

  // La configuración manda: sin edición vigente no hay overlay que pintar.
  //
  // RF-130 (criterio 1) — se pide la edición DEL AÑO que ya resolvió la disponibilidad, no "la más
  // reciente": antes de este fix el año no viajaba, así que en diciembre —con la edición de este año
  // y la del que empieza vigentes a la vez— la EDICIÓN pintada (fondo, título, slides, mensaje
  // genérico) podía ser de un año distinto al de los DATOS mostrados.
  const recap = useCurrentRecap(ouId, anio || undefined, disponible);
  const edition = recap.data;
  // Defensa en profundidad: aunque el backend ya filtra por año, si por lo que sea llegara una
  // edición de otro año, tratarla como "sin edición" en vez de pintarla es más seguro que confiar
  // ciegamente en la respuesta — `edition.anio` no se leía en NINGÚN punto de este archivo, y ese fue
  // literalmente el hallazgo original del criterio 1.
  const editionMatchesYear = edition != null && (!anio || edition.anio === anio);
  const slidesConfig: readonly RecapSlideDto[] = editionMatchesYear ? (edition?.slides ?? []) : [];

  // Datos: sólo si hay algo de datos que enseñar (con `soloGenerico` no se piden cifras del año).
  const needsData = disponible && !soloGenerico;
  // RF-130 F3 (H-07) — `fromMonth` es el `desdeMes` de la MISMA disponibilidad que ya resolvió el
  // año y el gate. Mismo `anio` y mismo `desdeMes` que pide `PerfilView` en su precarga: comparten
  // entrada de caché, así que abrir el overlay no dispara una petición extra.
  const wrapped = useMyWrapped(anio || undefined, availability?.desdeMes, needsData);
  const w: WrappedDto | undefined = wrapped.data;

  // Feed de recibidos — acotado al AÑO del Recap (H-13), ya no comparte entrada de caché con el del
  // Perfil: al agregar `desde`/`hasta` la query key difiere (`recognitionKeys.feed` incluye los
  // params completos), así que abrir el overlay dispara su propia petición. Es el costo aceptado de
  // cerrar H-13: antes, `pickBestMessage` filtraba por año SOBRE ESTAS 100 FILAS sin ventana
  // temporal, así que si el titular acumulaba más de 100 reconocimientos recibidos DESPUÉS del año
  // del Recap, ninguno del año correcto sobrevivía el corte de página y el slide `mensaje` se omitía
  // aunque sí hubiera mensajes — se leía como "sin dato" (RF-127) cuando en realidad el dato había
  // quedado fuera de la página. Con la ventana acotada al año, ese descarte ya no puede ocurrir.
  const desdeAnio = anio ? new Date(Date.UTC(anio, 0, 1, 0, 0, 0)).toISOString() : undefined;
  const hastaAnio = anio ? new Date(Date.UTC(anio, 11, 31, 23, 59, 59, 999)).toISOString() : undefined;
  const receivedFeed = useRecognitions(
    { receptorId: myId, page: 1, pageSize: 100, desde: desdeAnio, hasta: hastaAnio },
    needsData,
  );

  const hasWinnerSlide = slidesConfig.some((s) => s.clave === AUTO_ICON_KEY);
  // Sólo se pregunta por ganadores si el admin dejó ese slide encendido.
  const winners = useWinners(undefined, ouId, needsData && hasWinnerSlide);
  // Fila 40 — el catálogo de insignias, para comprobar que la medalla es del año del Recap. Se pide
  // bajo la MISMA condición que los ganadores: sin slide de ganador no hay nada que comprobar.
  const campaignBadges = useCampaignBadges(needsData && hasWinnerSlide);

  const receptor = useUserById(w?.topReceptorId);
  const emisor = useUserById(w?.topEmisorId);

  const topFund = w?.topFundamentalId
    ? catalog.find((f) => f.id === w.topFundamentalId)
    : undefined;
  // Bug Sesión #80 — el Fundamental MENOS usado ("tu reto"). `null`/ausente = sin envíos en el año
  // (nada que retar) o el id ya no está en el catálogo vigente; en ambos casos `renderReto` se queda
  // solo con el llamado a la acción genérico, sin inventar un Fundamental.
  const bottomFund = w?.bottomFundamentalId
    ? catalog.find((f) => f.id === w.bottomFundamentalId)
    : undefined;

  const received = w?.totales?.['Recibidos'] ?? 0;
  const sent = w?.totales?.['Enviados'] ?? 0;

  const bestMessage = useMemo(
    () => (anio ? pickBestMessage(receivedFeed.data?.items ?? [], anio) : null),
    [receivedFeed.data, anio],
  );
  // VIS-6 — «De {nombre}» solo con un nombre REAL: reparado en vivo si el snapshot quedó roto; sin
  // nombre (anónimo, tapado por una regla o no resuelto) la línea no se pinta, en vez de mostrar un id.
  const reparar = useReparadorDeNombres(useMemo(() => (bestMessage ? [bestMessage] : []), [bestMessage]));
  const remitenteDelMejorMensaje = bestMessage && !bestMessage.anonimo && bestMessage.emisorId
    ? nombreReparado(bestMessage.emisorId, bestMessage.emisorNombre, reparar, '') || null
    : null;
  const myWinner = useMemo(
    () => findMyWinner(winners.data ?? [], myId, anio, campaignBadges.data ?? []),
    [winners.data, myId, anio, campaignBadges.data],
  );

  /** Nombre del directorio, o `null` si no se puede resolver: un "—" sería relleno inventado. */
  const resolveName = (
    q: { data?: { displayName: string | null; email: string } | undefined },
    id: string | null | undefined,
  ): string | null => {
    if (!id) return null;
    if (!q.data) return null;
    return q.data.displayName ?? q.data.email;
  };

  /** Icono del slide: emoji, imagen, o nada. `ganador` nunca usa el icono editable (auto). */
  const renderIcon = (s: RecapSlideDto): ReactNode => {
    if (s.clave === AUTO_ICON_KEY) return null;
    if (!s.icono) return null;
    if (isImageIcon(s.icono)) {
      return <img src={s.icono} alt="" className="wrap-fund-icon" />;
    }
    return <span className="wrap-big-emoji" aria-hidden="true">{s.icono}</span>;
  };

  const titleOf = (s: RecapSlideDto): string => pickLocalized(s.titulo, lang);
  /** Etiqueta configurada bajo un número, con el rótulo del producto como respaldo. */
  const numberLabel = (s: RecapSlideDto, i: number, fallback: string): string =>
    s.etiquetasNumero?.[i]?.trim() || fallback;

  /** Cabecera común: el icono y el título SIEMPRE salen de la configuración del Recap. */
  const head = (s: RecapSlideDto): ReactNode => (
    <>
      {renderIcon(s)}
      <h2 className="wrap-title">{titleOf(s)}</h2>
    </>
  );

  // ── Un renderer por clave del catálogo ─────────────────────────────────────
  // Devolver `null` significa "este slide no tiene dato y SE OMITE" (RF-127). Van separados y no
  // como ramas de un `switch` para que cada regla de omisión se lea sola y se pueda cambiar sin
  // tocar las otras ocho.

  const renderPortada = (s: RecapSlideDto): ReactNode => {
    const mes = availability?.desdeMes;
    // `timeZone: 'UTC'` porque el instante se construye con `Date.UTC`: sin fijarla, `Intl` lo formatea
    // en la zona del navegador y el día 1 a medianoche UTC cae en el mes ANTERIOR en cualquier huso al
    // oeste de Greenwich — es decir, en México, LATAM y EE. UU., que son casi toda la plantilla. Un
    // titular que ingresó en septiembre leía "cubre desde agosto".
    const desde = mes
      ? new Intl.DateTimeFormat(lang, { month: 'long', timeZone: 'UTC' })
        .format(new Date(Date.UTC(anio, mes - 1, 1)))
      : null;
    return (
      <>
        {head(s)}
        {anio > 0 && <p className="wrap-year">{anio}</p>}
        <p className="wrap-sub">
          {desde
            ? t('wrapped.sinceMonth', { month: desde, defaultValue: 'Tu resumen cubre desde {{month}}.' })
            : t('wrapped.introHint')}
        </p>
      </>
    );
  };

  const renderNumeros = (s: RecapSlideDto): ReactNode => {
    if (received === 0 && sent === 0) return null;
    return (
      <>
        {head(s)}
        <div className="wrap-stat-row">
          <div className="wrap-stat-col">
            <h1 className="wrap-big-number">{received}</h1>
            <p className="wrap-label">{numberLabel(s, 0, t('profile.received'))}</p>
          </div>
          <div className="wrap-stat-col">
            <h1 className="wrap-big-number wrap-big-number--sm">{sent}</h1>
            <p className="wrap-label">{numberLabel(s, 1, t('profile.sent'))}</p>
          </div>
        </div>
        {/* La etiqueta de generosidad la calcula Analytics sobre los enviados del año. Sin ella el
            bloque no se pinta: el texto de respaldo que había antes era justamente el dato inventado
            que el RF prohíbe (D-2). `etiquetaGenerosidad` es una CLAVE, no texto — se resuelve contra
            la lista blanca de `GENEROSITY_TAG_KEYS`; una clave desconocida tampoco pinta nada. */}
        {w?.etiquetaGenerosidad && GENEROSITY_TAG_KEYS.has(w.etiquetaGenerosidad) && (
          <span className="wrap-tag">{t(`wrapped.generosityTag.${w.etiquetaGenerosidad}`)}</span>
        )}
      </>
    );
  };

  const renderDefine = (s: RecapSlideDto): ReactNode => {
    if (!topFund) return null;
    return (
      <>
        {head(s)}
        <h2 className="wrap-fund-name">{getFundamentalName(topFund, lang)}</h2>
        <p className="wrap-stat-sub">{t('wrapped.topFundamentalHint')}</p>
      </>
    );
  };

  const renderMensaje = (s: RecapSlideDto): ReactNode => {
    if (!bestMessage) return null;
    return (
      <>
        {head(s)}
        <div className="wrap-final-msg"><p>{bestMessage.mensaje}</p></div>
        {remitenteDelMejorMensaje && (
          <p className="wrap-stat-sub">
            {t('wrapped.messageFrom', { name: remitenteDelMejorMensaje, defaultValue: 'De {{name}}' })}
          </p>
        )}
      </>
    );
  };

  const renderQuienes = (s: RecapSlideDto): ReactNode => {
    const topEmisorName = resolveName(emisor, w?.topEmisorId);
    const topReceptorName = resolveName(receptor, w?.topReceptorId);
    if (!topEmisorName && !topReceptorName) return null;
    return (
      <>
        {head(s)}
        <div className="wrap-people-col">
          {topEmisorName && (
            <div>
              <p className="wrap-label">{numberLabel(s, 0, t('wrapped.topEmisor'))}</p>
              <p className="wrap-person-name">{topEmisorName}</p>
            </div>
          )}
          {topReceptorName && (
            <div>
              <p className="wrap-label">{numberLabel(s, 1, t('wrapped.topReceptor'))}</p>
              <p className="wrap-person-name">{topReceptorName}</p>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderRed = (s: RecapSlideDto): ReactNode => {
    if (!w || w.crossOu <= 0) return null;
    return (
      <>
        {head(s)}
        <h1 className="wrap-big-number">{w.crossOu}</h1>
        <p className="wrap-label">{numberLabel(s, 0, t('wrapped.crossOuValue', { count: w.crossOu }))}</p>
      </>
    );
  };

  const renderConstancia = (s: RecapSlideDto): ReactNode => {
    // RF-043 — sin récord no hay slide. Con la racha apagada en Gestión el Perfil pasa
    // `streakRecord: undefined`, y antes eso abría una pantalla entera con "0 semanas".
    if (streakRecord === undefined || streakRecord === null) return null;
    return (
      <>
        {head(s)}
        <h1 className="wrap-big-number">{streakRecord}</h1>
        <p className="wrap-stat-sub">
          {t(weeksKeyOf(streakRecord, streakUnitCopy(streakPeriodicidad).wrappedKeys),
             { count: streakRecord })}
        </p>
      </>
    );
  };

  /**
   * Bug Sesión #80 — nunca devuelve `null` (a diferencia de `renderDefine`): el título/llamado a la
   * acción configurado por el admin ya es contenido válido por sí solo. Con `bottomFund` resuelto se
   * concatena el Fundamental, igual que `renderDefine` con el top — mismo patrón, extremo opuesto.
   */
  const renderReto = (s: RecapSlideDto): ReactNode => (
    <>
      {head(s)}
      {bottomFund && (
        <>
          <h2 className="wrap-fund-name">{getFundamentalName(bottomFund, lang)}</h2>
          <p className="wrap-stat-sub">{t('wrapped.bottomFundamentalHint')}</p>
        </>
      )}
    </>
  );

  const renderGanador = (s: RecapSlideDto): ReactNode => {
    // "No saldrá insignia de ganador si no se cargaron ganadores". `head` no pinta icono para esta
    // clave: el suyo es la insignia real, no un emoji editable.
    if (!myWinner) return null;
    return (
      <>
        {head(s)}
        {/* QA (bug disfrazado) — la diapositiva solo anunciaba "eres ganador" sin decir de qué año.
            `anio` (no `myWinner.anio`) porque es el mismo valor que `findMyWinner` ya exigió que
            coincidiera para que `myWinner` exista — siempre hay un año real que mostrar aquí, sin
            depender de si el DTO trajo `anio` (ganadores anteriores a WP-D1 no lo traían). */}
        <p className="wrap-stat-sub">
          {t('wrapped.winnerYear', { anio, defaultValue: 'Año {{anio}}' })}
        </p>
        {myWinner.posicion != null && (
          <p className="wrap-stat-sub">
            {t('wrapped.winnerPosition', { position: myWinner.posicion, defaultValue: 'Lugar {{position}}' })}
          </p>
        )}
      </>
    );
  };

  const RENDERERS: Record<string, (s: RecapSlideDto) => ReactNode> = {
    portada: renderPortada,
    numeros: renderNumeros,
    define: renderDefine,
    mensaje: renderMensaje,
    quienes: renderQuienes,
    red: renderRed,
    constancia: renderConstancia,
    reto: renderReto,
    ganador: renderGanador,
  };

  /**
   * Contenido de un slide del catálogo, o `null` si se omite. Una clave desconocida (catálogo del
   * backend por delante de este cliente) también se omite: no se inventa nada para rellenarla.
   */
  const renderSlide = (s: RecapSlideDto): ReactNode => RENDERERS[s.clave]?.(s) ?? null;

  /** La tarjeta genérica de la edición ("Sigma te felicita"): imagen + textos configurados. */
  const genericCard = (msg: RecapGenericMessageDto): RenderedSlide => ({
    key: 'generico',
    fondo: estiloDeFondo({ fondo: slidesConfig[0]?.fondo ?? '#1a1a2e' }),
    content: (
      <>
        {msg.imagenUrl && <img src={msg.imagenUrl} alt="" className="wrap-fund-icon" />}
        <h2 className="wrap-title">{pickLocalized(msg.titulo, lang)}</h2>
        <div className="wrap-final-msg"><p>{pickLocalized(msg.descripcion, lang)}</p></div>
      </>
    ),
  });

  /**
   * Secuencia final. Se calcula en cada render (no memoizada) a propósito: depende de una decena de
   * consultas que llegan por separado, y una lista de dependencias es aquí una fuente de slides
   * congelados con datos viejos, no un ahorro medible.
   */
  const buildSlides = (): RenderedSlide[] => {
    // `!editionMatchesYear` cubre tanto "sin edición" como "edición de otro año" — en ambos casos no
    // hay nada que pintar, ni siquiera la tarjeta genérica (sería la de un año que no es el de los
    // datos que se muestran).
    if (!edition || !editionMatchesYear) return [];
    const generic = edition.mensajeGenerico;
    const canShowGeneric = hasGenericContent(generic, lang);

    // Ingreso muy tardío: UNA sola tarjeta y cero slides de datos.
    if (soloGenerico) return canShowGeneric ? [genericCard(generic)] : [];

    const built: RenderedSlide[] = [];
    let dataSlides = 0;
    // El backend ya entregó los slides activos y ordenados: se recorren tal cual.
    for (const s of slidesConfig) {
      const content = renderSlide(s);
      if (content === null) continue;
      if (!CONFIG_ONLY_KEYS.has(s.clave)) dataSlides += 1;
      built.push({ key: s.clave, fondo: estiloDeFondo(s), content });
    }

    // "Slide genérico cuando no hay datos": si ningún slide de datos sobrevivió, la portada y el
    // reto solos no son un Recap — se enseña el mensaje genérico de la edición, no un hueco.
    if (dataSlides === 0) return canShowGeneric ? [genericCard(generic)] : [];
    return built;
  };

  const slides: RenderedSlide[] = buildSlides();

  // Al recargar datos, no dejar el índice fuera de rango.
  useEffect(() => { setSlide((s) => (slides.length ? Math.min(s, slides.length - 1) : 0)); }, [slides.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') setSlide((s) => Math.min(slides.length - 1, s + 1));
      else if (e.key === 'ArrowLeft') setSlide((s) => Math.max(0, s - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, slides.length]);

  const current = slides[slide];
  const isLast = slide === slides.length - 1;
  const isLoading = availabilityQuery.isLoading || recap.isLoading || (needsData && wrapped.isLoading);
  // Estado honesto: sin disponibilidad, sin edición vigente o sin nada que enseñar, se dice.
  const isUnavailable = !isLoading && slides.length === 0;

  // 2026-11-03 — portal a `document.body`: montado dentro de `.plataforma-main` (que lleva
  // `isolation: isolate` en recognition-shell.css) su z-index competía solo dentro de ese contexto
  // de apilamiento y el Sidebar quedaba pintado encima del visor. Mismo arreglo que `RecapPreview`.
  // El teclado (Esc/flechas) escucha en `window`, así que no cambia con el portal.
  return createPortal((
    <div className="wrap-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="wrap-container" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="wrap-close" onClick={onClose} aria-label={t('common.close')}>
          <i className="fas fa-times" aria-hidden="true" />
        </button>

        {slides.length > 0 && (
          <div className="wrap-dots">
            {slides.map((s, i) => (
              <div key={s.key} className={`wrap-dot ${i === slide ? 'wrap-dot--active' : ''} ${i < slide ? 'wrap-dot--done' : ''}`} />
            ))}
          </div>
        )}

        {isLoading && <p className="wrap-state" role="status">{t('common.loading')}</p>}
        {isUnavailable && <p className="wrap-state" role="alert">{t('wrapped.unavailable')}</p>}

        {current && (
          <div className="wrap-slide" style={current.fondo} key={current.key}>
            <div className="wrap-slide-content">{current.content}</div>
          </div>
        )}

        {slides.length > 0 && (
          <div className="wrap-nav">
            <button
              type="button"
              className="wrap-nav-btn"
              disabled={slide === 0}
              onClick={() => setSlide((s) => Math.max(0, s - 1))}
              aria-label={t('wall.prev')}
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
            <span className="wrap-nav-count">{slide + 1}/{slides.length}</span>
            {isLast ? (
              <button type="button" className="wrap-nav-btn wrap-nav-btn--done" onClick={onClose} aria-label={t('common.close')}>
                <i className="fas fa-check" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="wrap-nav-btn"
                onClick={() => setSlide((s) => Math.min(slides.length - 1, s + 1))}
                aria-label={t('wall.next')}
              >
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  ), document.body);
}

export default WrappedModal;
